import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Package,
  FileQuestion,
  Video,
  FileText,
  Shield,
  LogOut,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Building2,
  Menu,
  Settings,
  UserCircle,
  Coins,
  Wallet,
  ChevronDown,
  Lock,
  MessageSquare,
  HelpCircle,
  Ticket,
  Key,
  TrendingDown,
  Bell,
  FlaskConical,
  Database,
  FolderOpen
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import axios from 'axios';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import BuyCreditsModal from './BuyCreditsModal';
import { NotificationCenter } from './NotificationCenter';

const API = process.env.REACT_APP_BACKEND_URL;

const adminMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Users, label: 'Users', path: '/admin/users' },
  { icon: GraduationCap, label: 'Universities', path: '/admin/universities' },
  { icon: Package, label: 'Packages', path: '/admin/packages' },
  { icon: FileQuestion, label: 'Question Bank', path: '/admin/questions' },
  { icon: Video, label: 'Interview Results', path: '/admin/interviews' },
  { icon: FileText, label: 'Create Interviews', path: '/admin/applications' },
  { icon: Users, label: 'Members', path: '/admin/members' },
  { icon: Shield, label: 'Roles & RBAC', path: '/admin/roles' },
  { icon: Ticket, label: 'Support Tickets', path: '/admin/support' },
  { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
  { divider: true, label: 'Interview Engine V2' },
  { icon: FlaskConical, label: 'V2 Dashboard', path: '/admin/v2' },
  { icon: Database, label: 'V2 Question Bank', path: '/admin/v2/questions' },
  { icon: FolderOpen, label: 'V2 Categories', path: '/admin/v2/categories' },
  { icon: Settings, label: 'V2 Settings', path: '/admin/v2/settings' },
  { icon: FlaskConical, label: 'Create Test Interview', path: '/admin/v2/create' },
  { icon: FileText, label: 'V2 Test History', path: '/admin/v2/history' },
];

const studentMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/student' },
  { icon: Video, label: 'My Interviews', path: '/student/interviews' },
  { icon: CreditCard, label: 'Credits & Billing', path: '/student/billing' },
];

const agentMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/agent' },
  { icon: Users, label: 'Partner Agents', path: '/agent/partner-agents' },
  { icon: FileText, label: 'Create Interviews', path: '/agent/applications' },
  { icon: Video, label: 'Interview Results', path: '/agent/interviews' },
  { icon: CreditCard, label: 'Credits & Billing', path: '/agent/billing' },
  { icon: Ticket, label: 'Support', path: '/support' },
  { icon: HelpCircle, label: 'Help Center', path: '/help' },
];

const organizationMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/organization' },
  { icon: FileText, label: 'Create Interviews', path: '/organization/applications' },
  { icon: FileQuestion, label: 'Question Bank', path: '/organization/questions' },
  { icon: Users, label: 'Agents', path: '/organization/agents' },
  { icon: Video, label: 'Interview Results', path: '/organization/interviews' },
  { icon: CreditCard, label: 'Credits & Billing', path: '/organization/billing' },
  { icon: UserCircle, label: 'Profile', path: '/organization/profile' },
  { icon: Ticket, label: 'Support', path: '/support' },
  { icon: HelpCircle, label: 'Help Center', path: '/help' },
];

export const DashboardLayout = ({ children, role = 'student' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { theme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orgBranding, setOrgBranding] = useState(null);
  const [credits, setCredits] = useState({ personal: 0, distributed: 0, allocated: 0, consumed: 0, allocatedBreakdown: [] });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  // Fetch credits for all roles
  useEffect(() => {
    if (token) {
      fetchCredits();
    }
  }, [token, role]);

  // Fetch organization branding for organization users
  useEffect(() => {
    if (role === 'organization' && token) {
      fetchOrgBranding();
    }
  }, [role, token]);

  const fetchCredits = async () => {
    try {
      let endpoint = '';
      switch (role) {
        case 'super_admin':
          // Admin doesn't have personal credits, skip
          return;
        case 'organization':
          endpoint = '/api/organization/dashboard';
          break;
        case 'agent':
          endpoint = '/api/agent/dashboard';
          break;
        default:
          endpoint = '/api/student/dashboard';
      }
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCredits({
        personal: response.data.credits || response.data.personal_credits || 0,
        distributed: response.data.distributed_credits || 0,
        allocated: response.data.allocated_credits || 0,
        consumed: response.data.consumed_credits || 0,
        allocatedBreakdown: response.data.allocated_credits_breakdown || []
      });
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  const fetchOrgBranding = async () => {
    try {
      const response = await axios.get(`${API}/api/organization/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgBranding(response.data);
    } catch (error) {
      console.error('Error fetching org branding:', error);
    }
  };

  const getMenuItems = () => {
    switch (role) {
      case 'super_admin': return adminMenuItems;
      case 'agent': return agentMenuItems;
      case 'organization': return organizationMenuItems;
      default: return studentMenuItems;
    }
  };

  const menuItems = getMenuItems();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get organization display info
  const getOrgDisplay = () => {
    if (role === 'organization') {
      return {
        name: orgBranding?.organization_name || user?.organization_name || user?.name || 'Organization',
        logo: orgBranding?.logo_url || null,
        color: orgBranding?.brand_color || '#84cc16'
      };
    }
    return null;
  };

  const orgDisplay = getOrgDisplay();

  const Sidebar = ({ mobile = false }) => (
    <div className={cn(
      "flex flex-col h-full bg-card border-r border-border",
      mobile ? "w-64" : collapsed ? "w-16" : "w-64",
      "transition-all duration-300"
    )}>
      {/* Logo / Organization Branding */}
      <div className="h-16 flex items-center px-4 border-b border-border">
        {role === 'organization' && orgDisplay ? (
          <Link to="/organization" className="flex items-center gap-2 overflow-hidden">
            {orgDisplay.logo ? (
              <img 
                src={orgDisplay.logo} 
                alt={orgDisplay.name}
                className="w-8 h-8 rounded-lg object-contain flex-shrink-0"
              />
            ) : (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: orgDisplay.color }}
              >
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            {(!collapsed || mobile) && (
              <span className="font-heading font-bold text-lg truncate">
                {orgDisplay.name}
              </span>
            )}
          </Link>
        ) : (
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-lime-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.png" alt="VisaInterviews.ai" className="w-6 h-6 object-contain" />
            </div>
            {(!collapsed || mobile) && (
              <span className="font-heading font-bold text-lg truncate">
                Visa<span className="text-lime-500">interviews</span>
              </span>
            )}
          </Link>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-2 space-y-1">
          {menuItems.map((item, idx) => {
            if (item.divider) {
              return (
                <div key={`divider-${idx}`} className="pt-4 pb-1">
                  {(!collapsed || mobile) && (
                    <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{item.label}</p>
                  )}
                  <div className="border-b border-border/40 mt-1" />
                </div>
              );
            }
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => mobile && setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive 
                    ? "bg-lime-500/10 text-lime-600 dark:text-lime-400" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(!collapsed || mobile) && (
                  <span className="font-medium text-sm truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-border space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className={cn("w-full justify-start gap-3", collapsed && !mobile && "justify-center")}
        >
          {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          {(!collapsed || mobile) && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowChangePassword(true)}
          className={cn("w-full justify-start gap-3", collapsed && !mobile && "justify-center")}
          data-testid="change-password-btn"
        >
          <Lock className="w-5 h-5" />
          {(!collapsed || mobile) && <span>Change Password</span>}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className={cn("w-full justify-start gap-3 text-destructive hover:text-destructive", collapsed && !mobile && "justify-center")}
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          {(!collapsed || mobile) && <span>Logout</span>}
        </Button>
      </div>

      {/* Collapse Toggle (Desktop only) */}
      {!mobile && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-20 -right-3 w-6 h-6 rounded-full border bg-card shadow-sm hover:bg-muted"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:block relative">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 z-50">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-border bg-card">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-semibold text-lg capitalize">
                {role === 'super_admin' ? 'Admin' : role.replace('_', ' ')} Dashboard
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Credits Display - Show for all roles except super_admin */}
            {role !== 'super_admin' && (
              <div className="hidden sm:flex items-center gap-3">
                {/* Total Credits - Show for agents, students, and organization */}
                {(role === 'agent' || role === 'student' || role === 'organization') && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <Coins className="w-4 h-4 text-purple-500" />
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground leading-none">Total Credits</p>
                      <p className="text-sm font-bold text-purple-500" data-testid="total-credits">
                        {role === 'organization' ? (credits.personal + credits.distributed) : (credits.personal + credits.allocated)}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Personal Credits - Always Show */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-lime-500/10 border border-lime-500/20">
                  <Wallet className="w-4 h-4 text-lime-500" />
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground leading-none">Personal Credits</p>
                    <p className="text-sm font-bold text-lime-500" data-testid="personal-credits">{credits.personal}</p>
                  </div>
                </div>
                
                {/* Allocated Credits - Show for agents and students */}
                {(role === 'agent' || role === 'student') && (
                  <div className="relative group">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 cursor-pointer hover:bg-blue-500/20 transition-colors">
                      <Building2 className="w-4 h-4 text-blue-500" />
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground leading-none">Allocated Credits</p>
                        <p className="text-sm font-bold text-blue-500" data-testid="allocated-credits">{credits.allocated}</p>
                      </div>
                      {credits.allocatedBreakdown.length > 1 && (
                        <ChevronDown className="w-3 h-3 text-blue-500 ml-1" />
                      )}
                    </div>
                    {/* Hover Dropdown */}
                    {credits.allocatedBreakdown.length > 0 && (
                      <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Allocated Credits Breakdown</p>
                        </div>
                        <div className="p-2 max-h-48 overflow-y-auto">
                          {credits.allocatedBreakdown.map((alloc, idx) => (
                            <div key={idx} className="flex items-center justify-between px-2 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <span className="text-sm truncate max-w-[140px]">{alloc.org_name || 'Organization'}</span>
                              </div>
                              <span className="text-sm font-bold text-blue-500">{alloc.credits || alloc.amount || 0}</span>
                            </div>
                          ))}
                        </div>
                        <div className="p-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg">
                          <div className="flex items-center justify-between px-2">
                            <span className="text-xs font-medium text-muted-foreground">Total Allocated</span>
                            <span className="text-sm font-bold text-blue-600">{credits.allocated}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Consumed Credits - Show for agents and students */}
                {(role === 'agent' || role === 'student') && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20">
                    <TrendingDown className="w-4 h-4 text-red-500" />
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground leading-none">Consumed</p>
                      <p className="text-sm font-bold text-red-500" data-testid="consumed-credits">{credits.consumed}</p>
                    </div>
                  </div>
                )}

                {/* Distributed Credits - Show for organization only */}
                {role === 'organization' && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <Coins className="w-4 h-4 text-amber-500" />
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground leading-none">Distributed</p>
                      <p className="text-sm font-bold text-amber-500" data-testid="distributed-credits">{credits.distributed}</p>
                    </div>
                  </div>
                )}

                {/* Buy Credits Button */}
                <Button
                  size="sm"
                  className="bg-lime-500 hover:bg-lime-600 text-white"
                  onClick={() => setShowBuyCredits(true)}
                  data-testid="buy-credits-btn"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Buy Credits
                </Button>
              </div>
            )}
            
            {/* Notification Bell - Super Admin only */}
            {role === 'super_admin' && <NotificationCenter />}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </Button>
            
            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2 hover:bg-muted">
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-lime-500/20 flex items-center justify-center">
                    <span className="text-lime-600 dark:text-lime-400 font-semibold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 md:hidden">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-4 h-4 mr-2" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4 mr-2" />
                      Dark Mode
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          {children}
        </main>
        
        {/* Footer */}
        <footer className="border-t border-border bg-card px-4 py-3">
          <p className="text-center text-xs text-muted-foreground">
            © 2026 International Education Office UK. All rights reserved.
          </p>
        </footer>
      </div>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={showChangePassword} 
        onOpenChange={setShowChangePassword} 
      />

      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        open={showBuyCredits} 
        onOpenChange={setShowBuyCredits}
        onSuccess={() => {
          fetchCredits();
          setShowBuyCredits(false);
        }}
      />
    </div>
  );
};

export default DashboardLayout;
